import type {
  LamaHierarchyComparisonOutput,
  LamaHierarchyResultOutput,
  LamaHierarchyView,
  LamaMaturityHierarchyView,
} from "@playbookai/widget-schemas";

/**
 * Shared presentation for the two v3 hierarchy widgets (2026-09-10 ADR-0027
 * amendment): Maturity Overview and Results by Pillar and Category. Pure
 * functions over the `lama/maturity-hierarchy@1` payload plus the host's
 * display-only `view`. No data access, no arithmetic on scores beyond
 * rounding at display — every percent and every delta arrives computed by the
 * provider on the matched assessed scope.
 */

/** The percent scale every v3 figure is drawn on. */
export const LAMA_PERCENT_MAX = 100;

export const LAMA_HIERARCHY_VIEW_LABEL: Record<LamaHierarchyView, string> = {
  baseline: "Baseline",
  responses: "Responses",
  compare: "Compare",
};

/** Whole percent at display, full precision underneath (D8). */
export function lamaPercentLabel(value: number | null | undefined): string {
  return value == null ? "—" : `${Math.round(value)}%`;
}

/** A change in percentage points, signed, whole at display. */
export function lamaPointsLabel(delta: number | null | undefined): string {
  if (delta == null) return "—";
  const rounded = Math.round(delta);
  if (rounded === 0) return "0 pts";
  return `${rounded > 0 ? "+" : "−"}${Math.abs(rounded)} pts`;
}

/** The accessible reading of a change against the baseline. */
export function lamaChangeDescription(delta: number): string {
  const rounded = Math.round(delta);
  if (rounded === 0) return "No change from baseline";
  const points = Math.abs(rounded);
  return `${rounded > 0 ? "Increase" : "Decrease"} of ${points} ${
    points === 1 ? "point" : "points"
  } from baseline`;
}

export interface HierarchyViewAvailability {
  readonly baseline: boolean;
  readonly responses: boolean;
  readonly compare: boolean;
}

/** Which views the payload can actually draw. A suppressed wave has no result. */
export function hierarchyViewAvailability(
  data: LamaMaturityHierarchyView,
): HierarchyViewAvailability {
  const baseline = data.baseline !== null;
  const responses = data.latestWave?.result != null;
  return {
    baseline,
    responses,
    compare: baseline && responses && data.comparison !== null,
  };
}

/**
 * The view to draw: the requested one when the payload can show it, otherwise
 * the closest one that exists (`compare` → whichever single source is there).
 * A view that is unavailable and has no fallback is kept, so the widget can
 * say WHY it is empty rather than silently drawing something else.
 */
export function effectiveHierarchyView(
  data: LamaMaturityHierarchyView,
): LamaHierarchyView {
  const available = hierarchyViewAvailability(data);
  const requested = data.view ?? "compare";
  if (available[requested]) return requested;
  if (requested === "compare") {
    if (available.responses) return "responses";
    if (available.baseline) return "baseline";
  }
  return requested;
}

/**
 * Why a view has nothing to draw, or null when it has. Suppression is a
 * DIFFERENT fact from "no responses" and is worded elsewhere (the widgets use
 * the shared suppression copy), so it is reported as its own reason.
 */
export function hierarchyMissingReason(
  data: LamaMaturityHierarchyView,
  view: LamaHierarchyView,
): "no-baseline" | "no-responses" | "suppressed" | null {
  const available = hierarchyViewAvailability(data);
  if (available[view]) return null;
  if (view === "baseline") return "no-baseline";
  if (data.latestWave === null) return "no-responses";
  if (data.latestWave.suppressed) return "suppressed";
  if (view === "compare" && !available.baseline) return "no-baseline";
  return "no-responses";
}

export const LAMA_NO_BASELINE_COPY =
  "No finalised baseline yet. A Transformation Lead can enter baseline results from the campaign.";
export const LAMA_NO_RESPONSES_COPY =
  "No responses yet. Figures appear once a wave has scored responses.";

export interface HierarchyProvenance {
  readonly baseline: string | null;
  readonly responses: string | null;
}

/** The bounded source pair, named: which revision and which wave the figures are. */
export function hierarchyProvenance(
  data: LamaMaturityHierarchyView,
): HierarchyProvenance {
  const baseline = data.baseline
    ? `Baseline · Revision ${data.baseline.revision} · assessed ${data.baseline.assessmentDate}`
    : null;
  const wave = data.latestWave;
  const responses = wave
    ? `Responses · ${wave.code} · ${
        wave.live ? "live, still open" : `closed ${wave.date.slice(0, 10)}`
      }`
    : null;
  return { baseline, responses };
}

export interface HierarchyCoverage {
  readonly categoryCount: number;
  readonly baselineAssessed: number | null;
  readonly responsesAssessed: number | null;
  readonly comparable: number | null;
}

/** The coverage strip: scope size, each side's assessed count, the overlap. */
export function hierarchyCoverage(
  data: LamaMaturityHierarchyView,
): HierarchyCoverage {
  return {
    categoryCount: data.scope.categories.length,
    baselineAssessed: data.baseline?.result.assessedCategoryCount ?? null,
    responsesAssessed: data.latestWave?.result?.assessedCategoryCount ?? null,
    comparable: data.comparison?.comparableCategoryCount ?? null,
  };
}

export interface HierarchyHeadline {
  readonly baseline: number | null;
  readonly responses: number | null;
  readonly delta: number | null;
  /** "4 of 6 pillars assessed" / "3 of 6 pillars comparable" */
  readonly coverage: string;
}

function unitOf(flat: boolean, one: "pillar" | "category"): string {
  return flat || one === "category" ? "categories" : "pillars";
}

/** The overall figure(s) of the active view, with their coverage clause. */
export function hierarchyHeadline(
  data: LamaMaturityHierarchyView,
  view: LamaHierarchyView,
): HierarchyHeadline | null {
  if (view === "compare") {
    const comparison = data.comparison;
    if (!comparison) return null;
    const coverage = comparison.flat
      ? `${comparison.comparableCategoryCount} of ${comparison.categoryCount} categories comparable`
      : `${comparison.comparablePillarCount} of ${comparison.pillarCount} pillars comparable`;
    return {
      baseline: comparison.baselinePercent,
      responses: comparison.responsesPercent,
      delta: comparison.deltaPoints,
      coverage,
    };
  }
  const result =
    view === "baseline"
      ? (data.baseline?.result ?? null)
      : (data.latestWave?.result ?? null);
  if (!result) return null;
  const coverage = result.flat
    ? `${result.assessedCategoryCount} of ${result.categoryCount} categories assessed`
    : `${result.assessedPillarCount} of ${result.pillarCount} pillars assessed`;
  return {
    baseline: view === "baseline" ? result.overallPercent : null,
    responses: view === "responses" ? result.overallPercent : null,
    delta: null,
    coverage,
  };
}

/** One radar axis / table row of the active view: a pillar, or a category on a flat version. */
export interface HierarchyAxisRow {
  readonly id: string;
  readonly label: string;
  readonly baseline: number | null;
  readonly responses: number | null;
  readonly delta: number | null;
  /** "2 of 3 categories" — assessed (or comparable) within the axis. */
  readonly coverage: string;
}

type ResultPillar = LamaHierarchyResultOutput["pillars"][number];
type ResultCategory = ResultPillar["categories"][number];
type ComparisonPillar = LamaHierarchyComparisonOutput["pillars"][number];
type ComparisonCategory = ComparisonPillar["categories"][number];

function resultRows(
  result: LamaHierarchyResultOutput,
  side: "baseline" | "responses",
): HierarchyAxisRow[] {
  const value = (percent: number | null) =>
    side === "baseline"
      ? { baseline: percent, responses: null }
      : { baseline: null, responses: percent };
  if (result.flat) {
    return result.ungroupedCategories.map((category) => ({
      id: category.categoryId,
      label: category.label,
      ...value(category.percent),
      delta: null,
      coverage:
        category.method === "direct"
          ? "Category-level score"
          : `${category.assessedQuestionCount} of ${category.questionCount} questions`,
    }));
  }
  return result.pillars.map((pillar) => ({
    id: pillar.pillarId,
    label: pillar.label,
    ...value(pillar.percent),
    delta: null,
    coverage: `${pillar.assessedCategoryCount} of ${pillar.categoryCount} categories`,
  }));
}

function comparisonRows(
  comparison: LamaHierarchyComparisonOutput,
): HierarchyAxisRow[] {
  if (comparison.flat) {
    return comparison.ungroupedCategories.map((category) => ({
      id: category.categoryId,
      label: category.label,
      baseline: category.baselinePercent,
      responses: category.responsesPercent,
      delta: category.deltaPoints,
      coverage:
        category.baselineMethod === "direct"
          ? category.deltaPoints === null
            ? "Not comparable"
            : "Category-level judgement"
          : `${category.comparableQuestionCount} of ${category.questionCount} questions comparable`,
    }));
  }
  return comparison.pillars.map((pillar) => ({
    id: pillar.pillarId,
    label: pillar.label,
    baseline: pillar.baselinePercent,
    responses: pillar.responsesPercent,
    delta: pillar.deltaPoints,
    coverage: `${pillar.comparableCategoryCount} of ${pillar.categoryCount} categories comparable`,
  }));
}

export function hierarchyAxisRows(
  data: LamaMaturityHierarchyView,
  view: LamaHierarchyView,
): HierarchyAxisRow[] {
  if (view === "compare") {
    return data.comparison ? comparisonRows(data.comparison) : [];
  }
  const result =
    view === "baseline"
      ? (data.baseline?.result ?? null)
      : (data.latestWave?.result ?? null);
  return result ? resultRows(result, view) : [];
}

/** Whether an axis row carries a point in the active view (both sides in compare). */
export function axisIsScored(
  row: HierarchyAxisRow,
  view: LamaHierarchyView,
): boolean {
  if (view === "compare") return row.delta !== null;
  return view === "baseline" ? row.baseline !== null : row.responses !== null;
}

/** "pillars" or "categories": what the axes of the active view are. */
export function hierarchyAxisUnit(
  data: LamaMaturityHierarchyView,
): "pillars" | "categories" {
  return unitOf(data.scope.flat, "pillar") as "pillars" | "categories";
}

// ---------------------------------------------------------------------------
// Results by Pillar and Category — the drill-down rows.

export interface HierarchyQuestionRow {
  readonly id: string;
  readonly prompt: string;
  readonly baseline: number | null;
  readonly responses: number | null;
  readonly delta: number | null;
}

export interface HierarchyCategoryRow {
  readonly id: string;
  readonly label: string;
  /** How the baseline side was entered, or how much was answered. */
  readonly detail: string;
  readonly baseline: number | null;
  readonly responses: number | null;
  readonly delta: number | null;
  /** Assessed / comparable in the active view. */
  readonly assessed: boolean;
  readonly questions: readonly HierarchyQuestionRow[];
  /** Why the expanded panel shows fewer questions than the category holds, or none. */
  readonly questionNote: string | null;
}

export interface HierarchySection {
  readonly id: string;
  readonly label: string;
  readonly baseline: number | null;
  readonly responses: number | null;
  readonly delta: number | null;
  readonly coverage: string;
  readonly categories: readonly HierarchyCategoryRow[];
}

function categoryIndex(
  result: LamaHierarchyResultOutput | null | undefined,
): ReadonlyMap<string, ResultCategory> {
  const index = new Map<string, ResultCategory>();
  if (!result) return index;
  for (const pillar of result.pillars) {
    for (const category of pillar.categories)
      index.set(category.categoryId, category);
  }
  for (const category of result.ungroupedCategories)
    index.set(category.categoryId, category);
  return index;
}

const NO_QUESTION_BREAKDOWN =
  "Category-level baseline entries have no question breakdown.";

function baselineDetail(category: ResultCategory | undefined): string {
  if (!category || category.percent === null) return "Not assessed in baseline";
  return category.method === "direct"
    ? "Baseline entered at category level"
    : "Baseline from question scores";
}

function resultCategoryRow(
  category: ResultCategory,
  side: "baseline" | "responses",
): HierarchyCategoryRow {
  const questions = category.questions.map((question) => ({
    id: question.questionId,
    prompt: question.prompt,
    baseline: side === "baseline" ? question.percent : null,
    responses: side === "responses" ? question.percent : null,
    delta: null,
  }));
  const direct = side === "baseline" && category.method === "direct";
  return {
    id: category.categoryId,
    label: category.label,
    detail:
      side === "baseline"
        ? baselineDetail(category)
        : category.percent === null
          ? "No responses"
          : `${category.assessedQuestionCount} of ${category.questionCount} questions answered`,
    baseline: side === "baseline" ? category.percent : null,
    responses: side === "responses" ? category.percent : null,
    delta: null,
    assessed: category.percent !== null,
    questions: direct ? [] : questions,
    questionNote: direct
      ? NO_QUESTION_BREAKDOWN
      : questions.length === 0
        ? "This category has no questions in scope."
        : null,
  };
}

function comparisonCategoryRow(
  category: ComparisonCategory,
  baselineIndex: ReadonlyMap<string, ResultCategory>,
  responsesIndex: ReadonlyMap<string, ResultCategory>,
): HierarchyCategoryRow {
  const baselineCategory = baselineIndex.get(category.categoryId);
  const responsesCategory = responsesIndex.get(category.categoryId);
  const detailParts = [baselineDetail(baselineCategory)];
  if (!responsesCategory || responsesCategory.percent === null) {
    detailParts.push("no responses");
  }
  const direct = category.baselineMethod === "direct";
  const comparable = category.deltaPoints !== null;
  let questionNote: string | null = null;
  if (direct) {
    questionNote = NO_QUESTION_BREAKDOWN;
  } else if (!comparable) {
    questionNote = "Not assessed on both sides, so nothing is compared.";
  } else if (category.comparableQuestionCount < category.questionCount) {
    questionNote = `${category.comparableQuestionCount} of ${category.questionCount} questions were assessed on both sides; the rest are not compared.`;
  }
  return {
    id: category.categoryId,
    label: category.label,
    detail: detailParts.join(" · "),
    baseline: category.baselinePercent,
    responses: category.responsesPercent,
    delta: category.deltaPoints,
    assessed: comparable,
    questions: category.questions.map((question) => ({
      id: question.questionId,
      prompt: question.prompt,
      baseline: question.baselinePercent,
      responses: question.responsesPercent,
      delta: question.deltaPoints,
    })),
    questionNote,
  };
}

/** Every pillar section (plus an ungrouped one) of the active view, scope order. */
export function hierarchySections(
  data: LamaMaturityHierarchyView,
  view: LamaHierarchyView,
): HierarchySection[] {
  const ungroupedLabel = data.scope.flat ? "Categories" : "Other categories";
  if (view === "compare") {
    const comparison = data.comparison;
    if (!comparison) return [];
    const baselineIndex = categoryIndex(data.baseline?.result);
    const responsesIndex = categoryIndex(data.latestWave?.result);
    const row = (category: ComparisonCategory) =>
      comparisonCategoryRow(category, baselineIndex, responsesIndex);
    const sections: HierarchySection[] = comparison.pillars.map((pillar) => ({
      id: pillar.pillarId,
      label: pillar.label,
      baseline: pillar.baselinePercent,
      responses: pillar.responsesPercent,
      delta: pillar.deltaPoints,
      coverage: `${pillar.comparableCategoryCount} of ${pillar.categoryCount} categories comparable`,
      categories: pillar.categories.map(row),
    }));
    if (comparison.ungroupedCategories.length > 0) {
      const comparable = comparison.ungroupedCategories.filter(
        (category) => category.deltaPoints !== null,
      ).length;
      sections.push({
        id: "ungrouped",
        label: ungroupedLabel,
        baseline: null,
        responses: null,
        delta: null,
        coverage: `${comparable} of ${comparison.ungroupedCategories.length} categories comparable`,
        categories: comparison.ungroupedCategories.map(row),
      });
    }
    return sections;
  }
  const result =
    view === "baseline"
      ? (data.baseline?.result ?? null)
      : (data.latestWave?.result ?? null);
  if (!result) return [];
  const row = (category: ResultCategory) => resultCategoryRow(category, view);
  const sections: HierarchySection[] = result.pillars.map((pillar) => ({
    id: pillar.pillarId,
    label: pillar.label,
    baseline: view === "baseline" ? pillar.percent : null,
    responses: view === "responses" ? pillar.percent : null,
    delta: null,
    coverage: `${pillar.assessedCategoryCount} of ${pillar.categoryCount} categories assessed`,
    categories: pillar.categories.map(row),
  }));
  if (result.ungroupedCategories.length > 0) {
    const assessed = result.ungroupedCategories.filter(
      (category) => category.percent !== null,
    ).length;
    sections.push({
      id: "ungrouped",
      label: ungroupedLabel,
      baseline: null,
      responses: null,
      delta: null,
      coverage: `${assessed} of ${result.ungroupedCategories.length} categories assessed`,
      categories: result.ungroupedCategories.map(row),
    });
  }
  return sections;
}
